import { atualizarCifSchema, criarCifSchema, type ApropriacaoEntrada, type AtualizarCifDto, type CifResponse, type CriarCifDto, type ItemCifResponse } from '@triade/contracts';
import { decimalDigitado, decimalParaTextoCampo } from '@/lib/format';

/**
 * Formulario do lancamento de CIF. Strings de tela; o corpo sai pelos schemas do contrato (criarCifSchema /
 * atualizarCifSchema). Nenhuma conta aqui: valor, custo e rateio vem da API e so sao exibidos ou repassados.
 */

export interface CifFormEntrada {
  identificador: string;
  descricao: string;
  numeroDocumento: string;
  /** "AAAA-MM" (input type=month). */
  competencia: string;
  /** "AAAA-MM-DD" (input type=date) ou vazio. */
  vencimento: string;
  /** Texto de campo (pt-BR). So existe com custo.ver. */
  valor: string;
  /** Id do fornecedor ou vazio = "Sem fornecedor". */
  fornecedorId: string;
  /** Item de CIF recorrente (so no lancamento novo; vazio = lancamento direto). Herda a apropriacao padrao na API. */
  itemCifId: string;
  /** Atalho "vai direto para o centro" (so no lancamento novo). Vale no lugar da apropriacao. */
  centroId: string;
}

/** Registro novo: competencia = a da busca da lista (ou o mes corrente, decidido por quem chama). */
export function cifVazio(competencia: string): CifFormEntrada {
  return {
    identificador: '',
    descricao: '',
    numeroDocumento: '',
    competencia,
    vencimento: '',
    valor: '',
    fornecedorId: '',
    itemCifId: '',
    centroId: '',
  };
}

/**
 * Ao escolher um item de CIF: identificador e descricao recebem o nome do item (cortado no limite do campo; so texto).
 * A apropriacao padrao nao e copiada aqui: a API a herda na criacao.
 */
export function preenchimentoDoItem(item: Pick<ItemCifResponse, 'nome'>): { identificador: string; descricao: string } {
  return { identificador: item.nome.slice(0, 60), descricao: item.nome.slice(0, 200) };
}

/** Registro da API para o formulario. `valor` so existe com custo.ver (senao fica vazio e nao vai no corpo). */
export function deCif(c: CifResponse): CifFormEntrada {
  return {
    identificador: c.identificador,
    descricao: c.descricao,
    numeroDocumento: c.numeroDocumento ?? '',
    competencia: c.competencia,
    vencimento: c.vencimento ? c.vencimento.slice(0, 10) : '',
    valor: c.valor !== undefined ? decimalParaTextoCampo(c.valor) : '',
    fornecedorId: c.fornecedorId ?? '',
    itemCifId: '',
    centroId: '',
  };
}

export type CampoCif = keyof CifFormEntrada;

export interface ErroCampoCif {
  campo: CampoCif | null;
  mensagem: string;
}

/**
 * Monta o corpo a partir da tela. `comCusto` = a sessao tem custo.ver (senao valor nao vai e a criacao nao e possivel).
 * `fornecedor` = false omite fornecedorId (quem nao ve fornecedores nao pode trocar o que ja esta gravado).
 * `apropriacao` null = nao envia.
 */
export function paraCorpo(
  v: CifFormEntrada,
  opcoes: { comCusto: boolean; fornecedor: boolean; apropriacao: ApropriacaoEntrada | null },
): { ok: true; corpo: Record<string, unknown> } | { ok: false; erro: ErroCampoCif } {
  const corpo: Record<string, unknown> = {
    identificador: v.identificador.trim(),
    descricao: v.descricao.trim(),
    numeroDocumento: v.numeroDocumento.trim() === '' ? null : v.numeroDocumento.trim(),
    competencia: v.competencia,
    vencimento: v.vencimento === '' ? null : v.vencimento,
  };

  if (opcoes.comCusto) {
    if (v.valor.trim() === '') return { ok: false, erro: { campo: 'valor', mensagem: 'Informe o valor da CIF' } };
    const valor = decimalDigitado(v.valor);
    if (valor === null) return { ok: false, erro: { campo: 'valor', mensagem: 'Informe um valor decimal (ex.: 12000 ou 12.000,50)' } };
    corpo.valor = valor;
  }

  if (opcoes.fornecedor) corpo.fornecedorId = v.fornecedorId === '' ? null : v.fornecedorId;
  // Item e centro so existem no lancamento novo (o formulario de edicao nao os mostra, entao vazios nao vao no corpo).
  if (v.itemCifId) corpo.itemCifId = v.itemCifId;
  if (v.centroId) corpo.centroId = v.centroId;
  if (opcoes.apropriacao && !v.centroId) corpo.apropriacao = opcoes.apropriacao;
  return { ok: true, corpo };
}

/** Valida com o schema do contrato (criar ou atualizar). A primeira issue vira o erro do campo. */
export function validarCorpo(
  corpo: Record<string, unknown>,
  modo: 'criar' | 'atualizar',
): { ok: true; dados: CriarCifDto | AtualizarCifDto } | { ok: false; erro: ErroCampoCif } {
  const r = modo === 'criar' ? criarCifSchema.safeParse(corpo) : atualizarCifSchema.safeParse(corpo);
  if (r.success) return { ok: true, dados: r.data };
  const i = r.error.issues[0];
  const campo = (i?.path[0] as CampoCif | undefined) ?? null;
  return { ok: false, erro: { campo, mensagem: i?.message ?? 'Dados inválidos' } };
}
